import React, {
  useCallback, useEffect, useReducer, useRef,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import axios from 'axios';
import { Alert, Button, Spin } from 'antd';
import csrfHeaders from '../../utils/csrfHeaders';
import './nextgenAppointmentHistory.scss';

// ENG-3660: the selected NextGen patient's kept appointments (date, rendering
// provider, location), past year first. Read-only, and never blocks the form.
const SEARCH_PATH = '/api/v1/nextgen_appointments/search';
const LOAD_ERROR = 'Could not load appointment history. Please try again.';
const EMPTY_RECENT = 'No kept appointments in the past year.';
const EMPTY_EARLIER = 'No earlier kept appointments.';
const NOT_AVAILABLE = 'Not available';

const initialState = {
  appointments: [],
  nextOffset: 0,
  earlierAvailable: false,
  loading: null, // 'recent' | 'earlier'
  loaded: false,
  loadedEarlier: false,
  error: null, // { message, mode }
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'reset':
      return initialState;
    case 'started':
      return { ...state, loading: action.mode, error: null };
    case 'succeeded':
      return {
        ...state,
        appointments: action.mode === 'earlier' ? [...state.appointments, ...action.appointments] : action.appointments,
        nextOffset: action.nextOffset,
        earlierAvailable: action.earlierAvailable,
        loading: null,
        loaded: true,
        loadedEarlier: state.loadedEarlier || action.mode === 'earlier',
      };
    case 'failed':
      return { ...state, loading: null, error: { message: action.message, mode: action.mode } };
    default:
      return state;
  }
};

const cancelledRequest = (error, controller) => (
  controller.signal.aborted || axios.isCancel(error) || error?.name === 'CanceledError'
);

// A 422 carries an actionable message (an expired patient selection, an
// unavailable practice); anything else gets the generic retry copy.
const errorMessage = (error) => {
  const detail = error?.response?.data?.error;
  return error?.response?.status === 422 && detail ? detail : LOAD_ERROR;
};

const formatDate = (value) => {
  const date = moment(value, 'YYYY-MM-DD', true);
  return date.isValid() ? date.format('MMM D, YYYY') : value;
};

const NextgenAppointmentHistory = ({
  credentialId, location, patientIdentifier, selectionProof,
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const requestRef = useRef(null);

  // The first page sends no offset; "Show earlier" always sends one, and 0 is
  // a valid place to resume when no visit is from the past year.
  const load = useCallback((mode, offset) => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    dispatch({ type: 'started', mode });

    axios.post(SEARCH_PATH, {
      emr_integration_credential_id: credentialId,
      location,
      patient_identifier: patientIdentifier,
      patient_selection_proof: selectionProof,
      ...(mode === 'earlier' && { offset }),
    }, { headers: csrfHeaders(), signal: controller.signal }).then((response) => {
      if (controller.signal.aborted) return;
      const data = response.data || {};
      dispatch({
        type: 'succeeded',
        mode,
        appointments: Array.isArray(data.appointments) ? data.appointments : [],
        nextOffset: Number.isInteger(data.next_offset) ? data.next_offset : (offset ?? 0),
        earlierAvailable: data.earlier_available === true,
      });
    }).catch((error) => {
      if (cancelledRequest(error, controller)) return;
      dispatch({ type: 'failed', mode, message: errorMessage(error) });
    });
  }, [credentialId, location, patientIdentifier, selectionProof]);

  useEffect(() => {
    dispatch({ type: 'reset' });
    if (!credentialId || !location || !patientIdentifier || !selectionProof) return undefined;

    load('recent');
    return () => requestRef.current?.abort();
  }, [credentialId, location, patientIdentifier, selectionProof, load]);

  const {
    appointments, nextOffset, earlierAvailable, loading, loaded, loadedEarlier, error,
  } = state;

  const handleRetry = () => {
    if (error?.mode === 'earlier') {
      load('earlier', nextOffset);
    } else {
      load('recent');
    }
  };

  // After "Show earlier" reaches the end of the list, say so: the button
  // disappearing on its own reads as nothing having happened.
  let emptyMessage = null;
  if (loaded && !loading && !error) {
    if (loadedEarlier && !earlierAvailable) {
      emptyMessage = EMPTY_EARLIER;
    } else if (appointments.length === 0) {
      emptyMessage = EMPTY_RECENT;
    }
  }

  return (
    <div className="nextgen-appointment-history" aria-busy={Boolean(loading)}>
      {loading === 'recent' && (
        <Spin size="small" tip="Loading appointment history..." />
      )}
      {appointments.length > 0 && (
        <table className="nextgen-appointment-history__table">
          <thead>
            <tr>
              <th scope="col">Date</th>
              <th scope="col">Provider</th>
              <th scope="col">Location</th>
            </tr>
          </thead>
          <tbody>
            {appointments.map((appointment, index) => (
              // Visits carry no ids by design; the list only ever grows at the end.
              // eslint-disable-next-line react/no-array-index-key
              <tr key={`${appointment.date}-${index}`}>
                <td className="nextgen-appointment-history__date">{formatDate(appointment.date)}</td>
                <td className={appointment.provider ? undefined : 'nextgen-appointment-history__missing'}>
                  {appointment.provider || NOT_AVAILABLE}
                </td>
                <td className={appointment.location ? undefined : 'nextgen-appointment-history__missing'}>
                  {appointment.location || NOT_AVAILABLE}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {emptyMessage && (
        <div className="nextgen-appointment-history__empty" role="status">{emptyMessage}</div>
      )}
      {error && (
        <Alert
          type="warning"
          showIcon
          message={error.message}
          action={<Button size="small" onClick={handleRetry}>Retry</Button>}
        />
      )}
      {loading === 'earlier' && <Spin size="small" tip="Loading earlier appointments..." />}
      {loaded && !loading && !error && earlierAvailable && (
        <Button
          type="link"
          className="nextgen-appointment-history__earlier"
          onClick={() => load('earlier', nextOffset)}
        >
          Show earlier
        </Button>
      )}
    </div>
  );
};

NextgenAppointmentHistory.propTypes = {
  credentialId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  location: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  patientIdentifier: PropTypes.string,
  selectionProof: PropTypes.string,
};

NextgenAppointmentHistory.defaultProps = {
  credentialId: undefined,
  location: undefined,
  patientIdentifier: undefined,
  selectionProof: undefined,
};

export default NextgenAppointmentHistory;
